/**
 * Zod schemas for the Wiring debug API response
 * (GET /api/v4/artifacts/:artifactId/wiring).
 *
 * The envelope is the server's own `WiringResponseSchema`
 * (`src/shared/schemas/wiring-debug.ts`). The server leaves the registry
 * entries `z.unknown()` so a registry field can never turn a debug read into
 * a 500; this client re-types them below so the debug tab can render them.
 * `wiring-api-registry-entries.test.ts` parses every real registry entry through these
 * schemas, so a registry change that drifts from them fails CI instead of
 * breaking the tab.
 */

import { z } from 'zod';
import type { ConfigField, ConfigFieldType } from '../../../../src/shared/view-system/registries/core';
import { WiringResponseSchema } from '../../../../src/shared/schemas/wiring-debug';

// Registry entry schemas for the in-code registries (handlers, interactions,
// artifact cards), typed to the registry interfaces `RegistryEntryCard` takes.

const CONFIG_FIELD_TYPES = [
  'text', 'url', 'email', 'number', 'boolean', 'textarea', 'select',
  'field_ref', 'field_ref_list', 'field_mapping_list', 'template_text',
  'component_ref', 'action_type_ref',
] as const satisfies readonly ConfigFieldType[]; // members checked; a new ConfigFieldType must be added here by hand (the real-registry test fails until it is)

const ConfigFieldSchema: z.ZodType<ConfigField> = z.object({
  key: z.string(),
  type: z.enum(CONFIG_FIELD_TYPES),
  label: z.string(),
  hint: z.string().optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  placeholder: z.string().optional(),
  purpose: z.string().optional(),
  userQuestion: z.string().optional(),
  options: z.array(z.object({
    value: z.string(),
    label: z.string(),
    description: z.string().optional(),
  })).optional(),
  get itemSchema() {
    return z.array(ConfigFieldSchema).optional();
  },
  registryFilter: z.object({ compatibleWith: z.array(z.string()).optional() }).optional(),
  visibleWhen: z.object({
    field: z.string(),
    operator: z.enum(['equals', 'not_equals', 'exists', 'not_exists']),
    value: z.string().optional(),
  }).optional(),
  layout: z.object({ width: z.enum(['full', 'half']).optional(), group: z.string().optional() }).optional(),
});

// ActionTypeEntrySchema imported from action-type.schema.ts

const RequirementEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  fieldKeys: z.array(z.string()),
  optional: z.boolean().optional(),
});

const HandlerEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  // Mirrors HandlerEntry.category in src/shared/view-system/registries/core.ts.
  category: z.enum(['webhook', 'crm_update', 'notification', 'internal', 'none']),
  configSchema: z.array(ConfigFieldSchema),
  wizardTabLabel: z.string().optional(),
  summaryTemplate: z.string().optional(),
  confirmVerb: z.string().optional(),
  mergeInteractionTab: z.boolean().optional(),
  requirements: z.array(RequirementEntrySchema).optional(),
  suggestedInteraction: z.string().optional(),
});

const InteractionEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  configSchema: z.array(ConfigFieldSchema),
  hasInputFields: z.boolean(),
  hasSurface: z.boolean(),
});

const ArtifactCardPreviewSchema = z.union([
  z.object({ type: z.literal('template_card'), showActions: z.boolean(), showFields: z.boolean() }),
  z.object({ type: z.literal('field_layout'), layout: z.enum(['vertical', 'horizontal', 'grid']), emphasis: z.enum(['title', 'data', 'metrics']) }),
  z.object({ type: z.literal('none'), reason: z.string() }),
]);

const ArtifactCardEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  supportsActions: z.boolean(),
  rendersActionButtons: z.boolean(),
  preview: ArtifactCardPreviewSchema,
  capabilities: z.object({
    supportsSelection: z.boolean(),
    supportsKeyboardNav: z.boolean(),
    supportsBulkActions: z.boolean(),
  }),
  slots: z.object({
    reserved: z.array(z.string()).readonly(),
    mode: z.enum(['card', 'item-root']),
  }),
  accent: z.string().optional(),
  sampleData: z.object({
    scenarios: z.array(z.object({ name: z.string(), fields: z.record(z.string(), z.string()) })),
    actions: z.array(z.object({
      id: z.string(),
      label: z.string(),
      isPrimary: z.boolean(),
      interaction: z.string(),
    })).optional(),
  }).optional(),
  tableColumns: z.object({
    columns: z.array(z.object({
      key: z.string(),
      label: z.string(),
      width: z.string().optional(),
      flex: z.boolean().optional(),
      sortable: z.boolean().optional(),
      align: z.enum(['right', 'left']).optional(),
    })),
    sampleRows: z.array(z.record(z.string(), z.string())).optional(),
    sampleActions: z.array(z.object({
      id: z.string(),
      label: z.string(),
      isPrimary: z.boolean(),
      interaction: z.string(),
    })).optional(),
  }).optional(),
  deprecated: z.boolean().optional(),
  migratesTo: z.string().optional(),
});

// Action: only what `WiringTab` reads. The route sends the resolved Action, whose
// `interactionConfig` varies by interaction (`{}` for `none`, `form` missing on
// some review actions), and `ActionInteractionSchema` rejects those shapes, so
// the config rides through untyped.

const WiringActionSchema = z.object({
  id: z.string(),
  interaction: z.string().nullish(),
  handler: z.string().nullish(),
  handlerConfig: z.unknown().optional(),
  interactionConfig: z.unknown().optional(),
  outcome: z.object({ status: z.string().nullish() }).nullish(),
  isTerminal: z.boolean().nullish(),
  isRepeatable: z.boolean().nullish(),
});

// Action type: a DB row mapped by the action-type cache, so nothing in it is
// guaranteed to sit inside today's enums: prod rows carry `defaultInteraction:
// 'modal'` (not an interaction id), `defaultOutcome: null`, and presets whose
// `interactionMode` is any string. Typed here to just the fields
// `RegistryEntryCard` shows, with the enum-valued ones as plain strings.
export const ActionTypeWireSchema = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string().nullish(),
  description: z.string().nullish(),
  category: z.string(),
  defaultInteraction: z.string(),
  isDecision: z.boolean(),
  executionLocation: z.string(),
  defaultOutcome: z.object({ status: z.string() }).nullish(),
  suggestedHandler: z.string().nullish(),
  requiredDataFields: z.array(z.object({ key: z.string(), type: z.string() })).nullish(),
  interactionPresets: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        description: z.string().nullish(),
        interactionMode: z.string(),
        inputFields: z.array(z.object({ key: z.string() })).nullish(),
      }),
    )
    .nullish(),
});
export type ActionTypeWire = z.infer<typeof ActionTypeWireSchema>;

// WiringActionData

export const WiringActionDataSchema = z.object({
  action: WiringActionSchema,
  actionType: ActionTypeWireSchema.nullable(),
  handler: HandlerEntrySchema.nullable(),
  interaction: InteractionEntrySchema.nullable(),
});

// WiringData

const wiringShape = WiringResponseSchema.shape;

export const WiringDataSchema = WiringResponseSchema.extend({
  blueprint: wiringShape.blueprint.pick({
    kind: true,
    displayName: true,
    intent: true,
    status: true,
    artifactCard: true,
    defaultView: true,
  }),
  wiring: wiringShape.wiring.extend({
    // `null` when the blueprint's `artifactCard` id is not in the registry.
    artifactCard: ArtifactCardEntrySchema.nullable(),
    actions: z.array(WiringActionDataSchema),
  }),
});
